import { apiFetch, parseApiError } from 'app/javascript/services/api';
import {
  BulkFacialResult,
  BulkFacialListResponse,
  CheckInSummary,
  ScheduleStatsResponse,
} from 'app/javascript/types';

const basePath = (slug: string) => `/api/v1/companies/${slug}/class_check_ins`;
const bulkFacialPath = (slug: string) => `/api/v1/companies/${slug}/bulk_facial_results`;

export const classCheckInsService = {
  async myStats(companySlug: string): Promise<{ month_count: number }> {
    const response = await apiFetch(`${basePath(companySlug)}/my_stats`);
    if (!response.ok) throw new Error('Failed to fetch stats');
    return response.json();
  },

  async bulkFacial(
    companySlug: string,
    photo: File,
  ): Promise<{
    id: number;
    status: string;
    suggested_schedule_id: number | null;
    suggested_date: string | null;
    photo_taken_at: string | null;
  }> {
    const formData = new FormData();
    formData.append('photo', photo);

    const response = await apiFetch(bulkFacialPath(companySlug), {
      method: 'POST',
      body: formData,
    });
    if (!response.ok) {
      throw new Error(await parseApiError(response, 'Erro ao enviar foto'));
    }
    return response.json();
  },

  async bulkFacialStatus(companySlug: string, resultId: number): Promise<BulkFacialResult> {
    const response = await apiFetch(`${bulkFacialPath(companySlug)}/${resultId}`);
    if (!response.ok) throw new Error('Erro ao verificar status');
    return response.json();
  },

  async bulkFacialList(companySlug: string, page = 1): Promise<BulkFacialListResponse> {
    const response = await apiFetch(`${bulkFacialPath(companySlug)}?page=${page}`);
    if (!response.ok) throw new Error('Erro ao carregar lista');
    return response.json();
  },

  async bulkFacialDestroy(companySlug: string, resultId: number): Promise<void> {
    const response = await apiFetch(`${bulkFacialPath(companySlug)}/${resultId}`, {
      method: 'DELETE',
    });
    if (!response.ok) throw new Error('Erro ao remover resultado');
  },

  async bulkFacialReprocess(companySlug: string, resultId: number): Promise<{ status: string }> {
    const response = await apiFetch(`${bulkFacialPath(companySlug)}/${resultId}/reprocess`, {
      method: 'POST',
    });
    if (!response.ok) {
      throw new Error(await parseApiError(response, 'Erro ao reprocessar'));
    }
    return response.json();
  },

  async bulkFacialAssignSchedule(
    companySlug: string,
    resultId: number,
    params: { class_schedule_id: number | null; class_date: string; class_time: string | null }
  ): Promise<void> {
    const response = await apiFetch(`${bulkFacialPath(companySlug)}/${resultId}`, {
      method: 'PATCH',
      body: JSON.stringify(params),
    });
    if (!response.ok) {
      throw new Error(await parseApiError(response, 'Erro ao identificar aula'));
    }
  },

  async createManualCheckIn(
    companySlug: string,
    resultId: number,
    userId: number,
  ): Promise<CheckInSummary> {
    const response = await apiFetch(
      `${bulkFacialPath(companySlug)}/${resultId}/class_check_ins`,
      {
        method: 'POST',
        body: JSON.stringify({ user_id: userId }),
      },
    );
    if (!response.ok) {
      throw new Error(await parseApiError(response, 'Erro ao fazer check-in'));
    }
    return response.json();
  },

  async deleteCheckIn(companySlug: string, checkInId: number): Promise<void> {
    const response = await apiFetch(`${basePath(companySlug)}/${checkInId}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error(await parseApiError(response, 'Erro ao remover check-in'));
    }
  },

  async scheduleStats(companySlug: string): Promise<ScheduleStatsResponse> {
    const response = await apiFetch(`${basePath(companySlug)}/schedule_stats`);
    if (!response.ok) throw new Error('Erro ao carregar estatísticas de frequência');
    return response.json();
  },
};
